/* eslint-disable */
import React from 'react';
import { Col, DatePicker, Form, Input, Row } from 'antd';
import InputLabel from 'components/InputLabel/index';
import { NumericFormat } from 'react-number-format';
import dayjs from 'dayjs';
import { withValueCap } from 'pages/globals/global';
import { constantFormValues } from '../../root';

const currentYear = dayjs().year();

/**
 * FinancialTermsConditions component renders the financial terms and conditions section
 * with form fields for the lease record management.
 *
 * This component should be rendered within a Form component context since it uses
 * Form.Item components that depend on the form context.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.screen - Current screen mode ('CREATE', 'EDIT', or 'VIEW')
 * @param {Object} props.constantFormValues - Form configuration values
 * @param {function} props.handleLeaseDateChange - Function to handle lease date changes
 * @param {Object} props.form - Form instance (needed for endOfFinancialYear field)
 * @param {Object} props.LeaseEndDate - Lease end date value
 *
 * @returns {JSX.Element} The Financial Terms & Conditions form section
 */
const FinancialTermsConditions = ({ screen, LeaseEndDate }) => {
	const form = Form.useFormInstance();
	const handleLeaseDateChange = () => {
		const leaseStartDate = form.getFieldValue('leaseStartDate');
		const leaseTerm = form.getFieldValue('leaseTerm');

		if (leaseStartDate && leaseTerm) {
			const leaseEndDate = leaseStartDate.add(leaseTerm, 'month').subtract(1, 'day'); // Add 5 months

			form.setFieldValue('leaseEndDate', leaseEndDate); // Updates leaseEndDate automatically
		}

		if (leaseStartDate) {
			form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], dayjs(leaseStartDate));
		}
	};
	console.count('FinancialTermsConditions');

	return (
		<Row gutter={[16, 0]}>
			<Col xs={4}>
				<InputLabel label={constantFormValues?.advanceAmount?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.advanceAmount?.key} rules={constantFormValues?.advanceAmount?.rules}>
					<NumericFormat
						size="large"
						decimalScale={2}
						placeholder="0.00"
						fixedDecimalScale
						thousandSeparator=","
						isAllowed={withValueCap}
						style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
						customInput={Input}
						disabled={screen === 'VIEW'}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.advanceDuration?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.advanceDuration?.key} rules={constantFormValues?.advanceDuration?.rules}>
					<Input type="number" disabled={screen === 'VIEW'} placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.grossRent?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item type="number" name={constantFormValues?.grossRent?.key} rules={constantFormValues?.grossRent?.rules}>
					<NumericFormat
						size="large"
						decimalScale={2}
						placeholder="0.00"
						fixedDecimalScale
						thousandSeparator=","
						isAllowed={withValueCap}
						style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
						customInput={Input}
						disabled={screen === 'VIEW'}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.leaseTerm?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.leaseTerm?.key} rules={constantFormValues?.leaseTerm?.rules}>
					<Input
						type="number"
						disabled={screen === 'VIEW'}
						placeholder="Write here"
						style={{ height: '32px', borderRadius: '4px' }}
						onChange={() => {
							handleLeaseDateChange();
						}}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.leaseStartDate?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item
					name={constantFormValues?.leaseStartDate?.key}
					rules={[
						{
							required: true,
							message: 'Field is required',
						},
					]}
				>
					<DatePicker
						disabled={screen === 'VIEW'}
						format="YYYY-MM-DD"
						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
						onChange={handleLeaseDateChange}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.leaseEndDate?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.leaseEndDate?.key} rules={constantFormValues?.leaseEndDate?.rules}>
					<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel type="number" label={constantFormValues?.escalationPercentage?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item type="number" name={constantFormValues?.escalationPercentage?.key} rules={constantFormValues?.escalationPercentage?.rules}>
					<Input disabled={screen === 'VIEW'} type="number" placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
				</Form.Item>
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.initialDiscountRate?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.initialDiscountRate?.key} rules={constantFormValues?.initialDiscountRate?.rules}>
					<Input type="number" disabled={screen === 'VIEW'} placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.upfrontLeasePayment?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.upfrontLeasePayment?.key} rules={constantFormValues?.upfrontLeasePayment?.rules}>
					<NumericFormat
						size="large"
						decimalScale={2}
						placeholder="0.00"
						fixedDecimalScale
						thousandSeparator=","
						isAllowed={withValueCap}
						style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
						customInput={Input}
						disabled={screen === 'VIEW'}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.initialDirectCost?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.initialDirectCost?.key} rules={constantFormValues?.initialDirectCost?.rules}>
					<NumericFormat
						size="large"
						decimalScale={2}
						placeholder="0.00"
						fixedDecimalScale
						thousandSeparator=","
						isAllowed={withValueCap}
						style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
						customInput={Input}
						disabled={screen === 'VIEW'}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.securityDeposit?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item name={constantFormValues?.securityDeposit?.key} rules={constantFormValues?.securityDeposit?.rules}>
					<NumericFormat
						size="large"
						decimalScale={2}
						placeholder="0.00"
						fixedDecimalScale
						thousandSeparator=","
						isAllowed={withValueCap}
						style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
						customInput={Input}
						disabled={screen === 'VIEW'}
					/>
				</Form.Item>
			</Col>

			<Col xs={4}>
				<InputLabel label={constantFormValues?.endOfFinancialYear?.label} head={true} />
			</Col>
			<Col xs={8}>
				<Form.Item
					name={constantFormValues?.endOfFinancialYear?.key}
					initialValue={dayjs().month(11).date(31)}
					// rules={[
					// 	{
					// 		required: false,
					// 	},
					// ]}
				>
					<DatePicker
						allowClear={false}
						disabled={screen === 'VIEW'}
						format="DD MMMM" // Display format shows only month and day
						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
						// defaultValue={dayjs().month(11).date(31)} // December 31st
						picker="date" // Still use date picker
						// disabledDate={() => false} // Don't disable any dates based on year
						// onChange={(date) => {
						// 	// When a date is selected, preserve only month and day
						// 	// and use current year
						// 	if (date) {
						// 		const currentYear = dayjs().year();
						// 		const newDate = date.year(currentYear);
						// 		form.setFieldsValue({
						// 			[constantFormValues?.endOfFinancialYear?.key]: newDate,
						// 		});
						// 	}
						// }}
						disabledDate={(current) => {
							return current.year() !== currentYear;
						}}
					/>
				</Form.Item>
			</Col>
		</Row>
	);
};

export default React.memo(FinancialTermsConditions);
